import { yupResolver } from '@hookform/resolvers/yup';
import { MoreHoriz } from '@mui/icons-material';
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material';
import i18next from 'i18next';
import { intersection } from 'lodash';
import React, { useMemo, useState } from 'react';
import { FieldPath, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Accordion } from 'src/components/basic/Accordion';
import { DintilhacText } from 'src/components/client/prejudiceFormComponents/DintilhacText';
import { ComputedPropsForm } from 'src/components/forms/ComputedPropsForm';
import { ComputedReadFieldForm } from 'src/components/forms/ComputedReadFieldForm';
import { PredefinedDatePickerForm } from 'src/components/forms/PredefinedDatePickerForm';
import { TextFieldForm } from 'src/components/forms/TextFieldForm';
import { ExpandableTextFieldForm } from 'src/components/forms/ExpandableTextFieldForm';
import { IMaskNumberProps, MaskNumber } from 'src/components/masks/MaskNumber';
import { ResourcesButton } from 'src/components/resources/ResourcesButton';
import { NormalTable } from 'src/components/styled';
import { CalculsFormScolaire } from 'src/constants/calculs/calculsFormScolaire';
import { scolaireDefaultValues } from 'src/constants/prejudice/defaultValues/Formscolaire.defaultValues';
import {
  getDefaultMinAndMaxDate,
  validationSchemaNumeroPieces,
  validationSchemaPrejudiceBase,
} from 'src/constants/prejudice/validation';
import { dateString } from 'src/helpers/yup';
import { useFormArrayUtils } from 'src/hooks/useFormArrayUtils';
import {
  PrejudiceFormScolaire,
  PrejudiceFormScolairePerteDeChance,
  PrejudiceFormScolairePerteDeChanceRow,
  PrejudiceType,
} from 'src/types/prejudice.type';
import { Procedure } from 'src/types/procedure.type';
import { Victime } from 'src/types/victime.type';
import * as yup from 'yup';
import { PrejudiceContainer } from '../PrejudiceContainer';
import { Chiffrage } from '../prejudiceFormComponents/Chiffrage';
import { DintilhacButton } from '../prejudiceFormComponents/DintilhacButton';
import { FormNumeroPieceDialog } from '../prejudiceFormComponents/FormNumeroPieceDialog';
import { TotalDeficit } from '../prejudiceFormComponents/TotalDeficit';
import { PrejudiceFormProps } from '../PrejudiceFormProps';
import { SavePrejudiceButton } from '../SavePrejudiceButton';
import { TooltipTableHeader } from '../TooltipTableHeader';

export const validationFormScolairePerteDeChanceRow = ({
  prejudiceType,
  victime,
  procedure,
}: {
  prejudiceType: PrejudiceType;
  victime: Victime;
  procedure: Procedure;
}): yup.ObjectSchema<PrejudiceFormScolairePerteDeChanceRow> => {
  const { minDate, maxDate } = getDefaultMinAndMaxDate({
    prejudiceType,
    victime,
    procedure,
  });
  return yup.object({
    numerosPieces: validationSchemaNumeroPieces,
    libelle: yup.string().required(),
    date: dateString()
      .required()
      .minDate(undefined, minDate.message, minDate.date)
      .maxDate(undefined, maxDate.message, maxDate.date),
    montant: yup.number().required(),
    coefficientPerteDeChance: yup
      .number()
      .required()
      .min(0)
      .max(100)
      .transform((value) => value / 100),
  });
};
export const validationFormScolairePerteDeChance = ({
  prejudiceType,
  victime,
  procedure,
}: {
  prejudiceType: PrejudiceType;
  victime: Victime;
  procedure: Procedure;
}): yup.ObjectSchema<PrejudiceFormScolairePerteDeChance> =>
  yup.object({
    rows: yup
      .array()
      .of(
        validationFormScolairePerteDeChanceRow({
          prejudiceType,
          victime,
          procedure,
        }),
      )
      .defined(),
    total: yup.number().required(),
  });

export const validationFormScolaire = ({
  prejudiceType,
  victime,
  procedure,
}: {
  prejudiceType: PrejudiceType;
  victime: Victime;
  procedure: Procedure;
}): yup.ObjectSchema<PrejudiceFormScolaire> =>
  validationSchemaPrejudiceBase.shape({
    montant: yup.number().required(i18next.t('forms.rules.required')),

    perteDeChance: validationFormScolairePerteDeChance({
      prejudiceType,
      victime,
      procedure,
    }),
    numerosPieces: validationSchemaNumeroPieces,
    total: yup.number().required(i18next.t('forms.rules.required')),
  });

const defaultScolairePerteDeChanceRow: PrejudiceFormScolairePerteDeChanceRow = {
  numerosPieces: { rows: [] },
  libelle: '',
  date: null,
  montant: 0,
  coefficientPerteDeChance: 100,
};

type Props = PrejudiceFormProps<PrejudiceFormScolaire>;

export const FormScolaire: React.FC<Props> = ({
  values,
  allNumerosPieces,
  displayTotalPartResponsabilite,
  partResponsabilite,
  ...props
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  const [showDintilhac, setShowDintilhac] = React.useState<boolean>(false);

  const useFormReturn = useForm<PrejudiceFormScolaire>({
    defaultValues: scolaireDefaultValues({
      values,
    }),
    resolver: yupResolver(
      validationFormScolaire({
        prejudiceType: props.prejudiceType,
        victime: props.victime,
        procedure: props.procedure,
      }),
    ),
    mode: 'onTouched',
  });
  const {
    control,
    formState: { errors },
  } = useFormReturn;
  const { remove, duplicate, fields, append } = useFormArrayUtils({
    control: useFormReturn.control,
    name: 'perteDeChance.rows',
    getValues: useFormReturn.getValues,
    setValue: useFormReturn.setValue,
  });

  const [isPerteDeChanceActive, setIsPerteDeChanceActive] = useState(false);
  const isPerteDeChanceError = useMemo(() => {
    const fields: FieldPath<PrejudiceFormScolaire>[] = ['perteDeChance'];
    return (
      intersection(Object.keys(errors), fields).length > 0 &&
      !isPerteDeChanceActive
    );
  }, [errors, isPerteDeChanceActive]);

  const handleMenuOpen =
    (index: number) => (event: React.MouseEvent<HTMLElement>) => {
      setOpenMenuIndex(index);
      setAnchorEl(event.currentTarget);
    };

  const handleMenuClose = () => {
    setOpenMenuIndex(null);
    setAnchorEl(null);
  };
  const handleDelete = (index: number) => {
    remove(index);
    handleMenuClose();
  };

  const handleDuplicate = (index: number) => {
    duplicate(index);
    handleMenuClose();
  };

  const { minDate, maxDate } = getDefaultMinAndMaxDate({
    prejudiceType: props.prejudiceType,
    victime: props.victime,
    procedure: props.procedure,
  });

  return (
    <PrejudiceContainer<PrejudiceFormScolaire>
      {...props}
      {...useFormReturn}
      renderPrejudice={() => (
        <Stack spacing={2}>
          <ExpandableTextFieldForm
            control={control}
            name="notes"
            label={t('prejudice.fields.notes.label')}
            placeholder={t('prejudice.fields.notes.placeholder') || ''}
            dialogTitle={t('prejudice.fields.notes.label')}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <DintilhacButton onClick={() => setShowDintilhac(true)} />
                </InputAdornment>
              ),
            }}
            multiline
            maxRows={10}
            fullWidth
          />
          <DintilhacText
            open={showDintilhac}
            setOpen={setShowDintilhac}
            label={t('prejudice.fields.dintilhac.label')}
            content={i18next.t(
              `prejudice.prejudicesTypes.${props.prejudiceType}.introduction`,
              {
                defaultValue: '',
              },
            )}
          />
          <FormNumeroPieceDialog
            control={control}
            name="numerosPieces"
            displayNumeroPieceText={true}
            allOtherNumeroPieces={allNumerosPieces}
          />
          <TextFieldForm
            control={control}
            name="montant"
            label={t(
              'prejudice.prejudicesFormTypes.SCOLAIRE.fields.montant.label',
            )}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputComponent: MaskNumber as any,
              inputProps: {
                suffix: '€',
                numberMask: { scale: 2 },
              } as IMaskNumberProps,
            }}
            sx={{ minWidth: '268px' }}
          />
          <Accordion
            title={t(
              'prejudice.prejudicesFormTypes.SCOLAIRE.fields.perteDeChance.accordionTitle',
            )}
            isError={isPerteDeChanceError}
            errorTooltip={
              t(
                'prejudice.prejudicesFormTypes.SCOLAIRE.fields.perteDeChance.errorTooltip',
              ) || ''
            }
            expanded={isPerteDeChanceActive}
            onChange={() => setIsPerteDeChanceActive(!isPerteDeChanceActive)}
          >
            <>
              <TableContainer>
                <NormalTable>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        {t('numeroPiece.form.columnHeader')}
                      </TableCell>
                      <TableCell align="center">
                        {t(
                          'prejudice.prejudicesFormTypes.SCOLAIRE.fields.perteDeChance.rows.libelle.columnHeader',
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {t(
                          'prejudice.prejudicesFormTypes.SCOLAIRE.fields.perteDeChance.rows.date.columnHeader',
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {t(
                          'prejudice.prejudicesFormTypes.SCOLAIRE.fields.perteDeChance.rows.montant.columnHeader',
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <TooltipTableHeader
                          tooltipTitle={t(
                            'prejudice.perteDeChance.fields.coefficientPerteDeChance.headerTooltip',
                          )}
                          headerLabel={t(
                            'prejudice.prejudicesFormTypes.SCOLAIRE.fields.perteDeChance.rows.coefficientPerteDeChance.columnHeader',
                          )}
                        />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((field, index) => (
                      <TableRow key={field.id}>
                        <TableCell align="center">
                          <FormNumeroPieceDialog
                            control={control}
                            name={`perteDeChance.rows.${index}.numerosPieces`}
                            allOtherNumeroPieces={allNumerosPieces}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <ExpandableTextFieldForm
                            control={control}
                            name={`perteDeChance.rows.${index}.libelle`}
                            dialogTitle={t('prejudice.prejudicesFormTypes.SCOLAIRE.fields.perteDeChance.rows.libelle.columnHeader')}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="center">
                          <PredefinedDatePickerForm
                            control={control}
                            name={`perteDeChance.rows.${index}.date`}
                            minDate={minDate.date}
                            maxDate={maxDate.date}
                            procedure={props.procedure}
                            victime={props.victime}
                            prejudiceType={props.prejudiceType}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextFieldForm
                            control={control}
                            name={`perteDeChance.rows.${index}.montant`}
                            InputProps={{
                              inputComponent: MaskNumber as any,
                              inputProps: {
                                numberMask: { scale: 2 },
                                suffix: '€',
                              } as IMaskNumberProps,
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextFieldForm
                            control={control}
                            name={`perteDeChance.rows.${index}.coefficientPerteDeChance`}
                            InputProps={{
                              inputComponent: MaskNumber as any,
                              inputProps: {
                                numberMask: { scale: 2, min: 0, max: 100 },
                                suffix: '%',
                              } as IMaskNumberProps,
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton onClick={handleMenuOpen(index)}>
                            <MoreHoriz />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={openMenuIndex === index}
                            onClose={handleMenuClose}
                          >
                            <MenuItem onClick={() => handleDelete(index)}>
                              {t('forms.table.deleteRow', { index: index + 1 })}
                            </MenuItem>
                            <MenuItem onClick={() => handleDuplicate(index)}>
                              {t('forms.table.duplicateRow', {
                                index: index + 1,
                              })}
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={5} align="right">
                        <ComputedReadFieldForm
                          control={control}
                          name="perteDeChance.total"
                          watchFields={['perteDeChance.rows']}
                          compute={([rows]) =>
                            CalculsFormScolaire.getTotalPerteDeChance(
                              rows.map((row) => ({
                                ...row,
                                coefficientPerteDeChance:
                                  row.coefficientPerteDeChance / 100,
                              })),
                            )
                          }
                          InputProps={{
                            inputComponent: MaskNumber as any,
                            inputProps: {
                              numberMask: { scale: 2 },
                              suffix: '€',
                            } as IMaskNumberProps,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </NormalTable>
              </TableContainer>
              <Stack alignItems="center">
                <Button onClick={() => append(defaultScolairePerteDeChanceRow)}>
                  {t('forms.table.addRow')}
                </Button>
              </Stack>
            </>
          </Accordion>
          <ComputedReadFieldForm
            control={control}
            name="total"
            watchFields={['montant', 'perteDeChance.total']}
            compute={([montant, totalPerteDeChance]) =>
              CalculsFormScolaire.getTotal(montant, totalPerteDeChance)
            }
            InputProps={{
              inputComponent: MaskNumber as any,
              inputProps: {
                numberMask: { scale: 2 },
                suffix: '€',
              } as IMaskNumberProps,
            }}
          />
          {displayTotalPartResponsabilite && (
            <ComputedPropsForm
              control={control}
              watchFields={['total']}
              compute={([total]) => {
                return {
                  props: {
                    totalPartResponsabilite: total * (partResponsabilite || 0),
                    total,
                    partResponsabilite,
                  },
                };
              }}
              render={({
                totalPartResponsabilite,
                total,
                partResponsabilite,
              }) => (
                <Stack alignSelf="end" maxWidth="400px">
                  <TotalDeficit
                    totalPartResponsabilite={totalPartResponsabilite}
                    totalMontantTotal={total}
                    displayPartResponsabilite={displayTotalPartResponsabilite}
                    partResponsabilite={partResponsabilite}
                  />
                </Stack>
              )}
            />
          )}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Chiffrage control={control} />
            </Grid>
            <Grid
              item
              xs={4}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <SavePrejudiceButton victime={props.victime} />
            </Grid>
            <Grid item xs={4} display="flex" justifyContent="flex-end">
              <ResourcesButton baremeType="Scolaire" isMultipleBaremes />
            </Grid>
          </Grid>
        </Stack>
      )}
    />
  );
};
